import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import styled from "styled-components";
import * as Yup from "yup";
import { loginSchema } from "../utils/validationSchemas";
import { loginUser, generateCertUrl, validateOfferToken } from "../services/api";
import { FONTS, COLORS, BREAKPOINTS, SHADOWS } from "../utils/styleConstants";
import sidebarLogo from "../assets/sidebar_logo.svg";
import headerLogo from "../assets/header_logo.svg";
import { useAuth } from "../context/AuthContext";
import LoginFooter from "../components/LoginFooter";
import loginBackground from "../assets/login_background.svg";
import MobileLoginForm from "./MobileLoginForm";
import OfferPopup from "./OfferPopup.js";


// Styled components
const FormContainer = styled.div`
  width: 100%;
  height: 100vh;
  background-image: url(${loginBackground});
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  opacity: ${props => props.$loading ? 0 : 1};

  @media (min-width: 1920px) {
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
  }

  /* Aseguramos que el footer siempre esté al final y centrado */
  footer {
    margin-top: auto;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    
    /* Aseguramos que los enlaces estén centrados */
    nav {
      display: flex;
      justify-content: center;
      gap: 1rem;
      width: 100%;
    }

    a {
      text-align: center;
    }
  }
`;

const TopLogo = styled.img`
  width: min(40px, 5vw);
  height: auto;
  position: absolute;
  top: min(2rem, 4vh);
  left: min(2rem, 4vw);
  z-index: 2;
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  padding: 0 max(2rem, 4vw);
  gap: min(4rem, 8vw);

  @media (max-width: 1600px) {
    gap: min(3rem, 6vw);
    padding: 0 max(1.75rem, 3.5vw);
  }

  @media (max-width: 1400px) {
    gap: min(2rem, 4vw);
    padding: 0 max(1.5rem, 3vw);
  }

  @media (max-width: 1200px) {
    gap: min(1.5rem, 3vw);
    padding: 0 max(1.25rem, 2.5vw);
  }

  @media (max-width: 1000px) {
    gap: min(1rem, 2vw);
    padding: 0 max(1rem, 2vw);
  }

  @media (max-width: 800px) {
    gap: min(0.75rem, 1.5vw);
    padding: 0 max(0.75rem, 1.5vw);
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 1 45%;
  position: relative;
  padding-left: min(2rem, 4vw);
  margin-right: auto;

  @media (max-width: 1400px) {
    padding-left: min(1.5rem, 3vw);
  }

  @media (max-width: 1200px) {
    padding-left: min(1rem, 2vw);
    flex: 0 1 40%;
    align-items: flex-start;
    gap: min(1rem, 2vh);
  }

  @media (max-width: 1000px) {
    padding-left: min(0.5rem, 1vw);
    flex: 0 1 35%;
    gap: min(0.75rem, 1.5vh);
  }

  @media (max-width: 800px) {
    gap: min(0.5rem, 1vh);
  }
`;

const HeaderLogo = styled.img`
  width: min(50px, 6vw);
  height: auto;
  position: absolute;
  top: min(2rem, 4vh);
  left: min(2.5rem, 5vw);

  @media (max-width: 1400px) {
    width: min(40px, 5vw);
  }

  @media (max-width: 1200px) {
    width: min(35px, 4vw);
    position: relative;
    top: min(1rem, 2vh);
    left: min(1.5rem, 3vw);
    margin-bottom: min(0.75rem, 1.5vh);
  }

  @media (max-width: 1000px) {
    width: min(30px, 3.5vw);
    margin-bottom: min(0.5rem, 1vh);
  }

  @media (max-width: 800px) {
    margin-bottom: min(0.375rem, 0.75vh);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: min(1rem, 2vh);
  margin-left: min(16rem, 20%);
  margin-top: min(1rem, 2vh);
  position: relative;
  z-index: 1;

  @media (max-width: 1400px) {
    margin-left: calc(min(8rem, 12%) + 5px);
  }

  @media (max-width: 1200px) {
    margin-left: 0;
    margin-top: min(0.5rem, 1vh);
    align-items: flex-start;
    padding-left: min(1.5rem, 3vw);
  }

  @media (max-width: 1000px) {
    padding-left: min(1rem, 2vw);
  }
`;

const FormTitle = styled.h1`
  color: #ff9773;
  font-size: min(4rem, 5vw);
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0;
  white-space: nowrap;

  @media (max-width: 1600px) {
    font-size: min(3.6rem, 4.6vw);
  }

  @media (max-width: 1400px) {
    font-size: min(3.2rem, 4.2vw);
  }

  @media (max-width: 1200px) {
    font-size: min(2.8rem, 3.8vw);
  }

  @media (max-width: 1000px) {
    font-size: min(2.6rem, 3.6vw);
  }

  @media (max-width: 800px) {
    font-size: min(2.4rem, 3.4vw);
  }
`;

const FormSubtitle = styled.p`
  font-size: min(1.5rem, 2.5vw);
  font-family: ${FONTS.PRIMARY};
  font-weight: 400;
  margin: 0;
  line-height: 1.5;
  color: #ff9773;
  white-space: pre-line;

  @media (max-width: 1600px) {
    font-size: min(1.4rem, 2.3vw);
  }

  @media (max-width: 1400px) {
    font-size: min(1.3rem, 2.1vw);
  }

  @media (max-width: 1200px) {
    font-size: min(1.2rem, 1.9vw);
  }

  @media (max-width: 1000px) {
    font-size: min(1.1rem, 1.7vw);
  }

  @media (max-width: 800px) {
    font-size: min(1rem, 1.5vw);
  }
`;

const RightSection = styled.div`
  flex: 0 1 600px;
  padding: min(2rem, 4vw);
  margin-top: min(7rem, 10vh);
  margin-right: min(4rem, 5vw);
  position: relative;
  z-index: 1;

  @media (min-width: 1920px) {
    margin-right: min(6rem, 8vw);
  }

  @media (max-width: 1600px) {
    flex: 0 1 450px;
    margin-right: min(3.5rem, 4.5vw);
    padding: min(1.75rem, 3.5vw);
  }

  @media (max-width: 1400px) {
    flex: 0 1 400px;
    margin-right: min(3rem, 4vw);
    padding: min(1.5rem, 3vw);
  }

  @media (max-width: 1200px) {
    flex: 0 1 350px;
    margin-right: min(2.5rem, 3.5vw);
    padding: min(1.25rem, 2.5vw);
  }

  @media (max-width: 1000px) {
    flex: 0 1 325px;
    margin-right: min(2rem, 3vw);
    padding: min(1rem, 2vw);
  }

  @media (max-width: 800px) {
    flex: 0 1 300px;
    margin-right: min(1.5rem, 2.5vw);
    padding: min(0.75rem, 1.5vw);
  }
`;

const Label = styled.label`
  font-size: min(1.7rem, 2.5vw);
  color: #000049;
  font-family: ${FONTS.SECONDARY};
  font-weight: 700;
  min-width: min(180px, 25%);
  white-space: nowrap;

  /* Ajuste específico para el label del DNI */
  &[for="id_number"] {
    min-width: min(160px, 22%);
    font-size: min(1.5rem, 2.2vw);
    
    @media (max-width: 1600px) {
      min-width: min(150px, 20%);
      font-size: min(1.3rem, 2vw);
    }

    @media (max-width: 1400px) {
      min-width: min(140px, 18%);
      font-size: min(1.2rem, 1.8vw);
    }

    @media (max-width: 1200px) {
      min-width: min(130px, 16%);
      font-size: min(1.1rem, 1.6vw);
    }

    @media (max-width: 1000px) {
      min-width: min(120px, 14%);
      font-size: min(1rem, 1.4vw);
    }

    @media (max-width: 900px) {
      min-width: min(110px, 12%);
      font-size: min(0.9rem, 1.2vw);
    }
  }

  @media (max-width: 1600px) {
    font-size: min(1.6rem, 2.3vw);
    min-width: min(170px, 23%);
  }

  @media (max-width: 1400px) {
    font-size: min(1.5rem, 2.1vw);
    min-width: min(160px, 21%);
  }

  @media (max-width: 1200px) {
    font-size: min(1.4rem, 1.9vw);
    min-width: min(150px, 19%);
  }

  @media (max-width: 1000px) {
    font-size: min(1.3rem, 1.7vw);
    min-width: min(140px, 17%);
  }

  @media (max-width: 800px) {
    font-size: min(1.2rem, 1.5vw);
    min-width: min(130px, 15%);
  }
`;

const Input = styled.input`
  padding: 0.25rem 0;
  border: none;
  font-size: min(1.4rem, 2vw);
  font-family: "WorkSans", "Avenir Next", sans-serif;
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;
  margin-right: min(1rem, 2vw);
  color: #000049;

  @media (max-width: 1600px) {
    font-size: min(1.35rem, 1.9vw);
  }

  @media (max-width: 1400px) {
    font-size: min(1.3rem, 1.8vw);
  }

  @media (max-width: 1200px) {
    font-size: min(1.25rem, 1.7vw);
  }

  @media (max-width: 1000px) {
    font-size: min(1.2rem, 1.6vw);
  }

  @media (max-width: 800px) {
    font-size: min(1.15rem, 1.5vw);
  }

  &::placeholder {
    color: rgba(0, 0, 73, 0.5);
  }

  &:focus {
    outline: none;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px transparent inset !important;
    -webkit-text-fill-color: #000049 !important;
    transition: background-color 5000s ease-in-out 0s;
    font-size: inherit !important;
  }
`;

const PasswordField = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  padding-right: min(40px, 5vw);
`;

const PasswordInput = styled(Input)`
  width: 100%;
  padding-right: 0;
  border: none;
  margin-right: 0;

  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
  }

  /* Eliminar fondo blanco del autocompletado y mantener tamaño de fuente */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px transparent inset !important;
    -webkit-text-fill-color: #000049 !important;
    transition: background-color 5000s ease-in-out 0s;
    font-size: 1.4rem !important;
  }

  @media (max-width: 768px) {
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      font-size: 1.2rem !important;
    }
  }
`;

const EyeIcon = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #000049;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const HorizontalField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: min(1.5rem, 3vh);
  position: relative;
  height: min(3rem, 6vh);

  .input-row {
    display: flex;
    align-items: center;
    gap: min(0.5rem, 1vw);
    height: min(2.5rem, 5vh);
  }

  ${Label} {
    margin-bottom: 0;
    flex: 0 0 auto;
  }

  ${Input}, ${PasswordInput} {
    flex: 1;
    border-bottom: none;
  }

  ${PasswordField} {
    flex: 1;
  }

  padding-bottom: 0.25rem;
  border-bottom: 1px solid #ccc;

  &:focus-within {
    border-bottom-color: var(--primary-color);
  }

  @media (max-width: 1400px) {
    margin-bottom: min(1.2rem, 2.4vh);
    height: min(2.8rem, 5.6vh);

    .input-row {
      height: min(2.3rem, 4.6vh);
    }
  }

  @media (max-width: 1200px) {
    margin-bottom: min(1rem, 2vh);
    height: min(2.5rem, 5vh);

    .input-row {
      height: min(2rem, 4vh);
    }
  }

  @media (max-width: 1000px) {
    margin-bottom: min(0.8rem, 1.6vh);
    height: min(2.2rem, 4.4vh);

    .input-row {
      height: min(1.8rem, 3.6vh);
    }
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  font-size: min(0.875rem, 1.5vw);
  margin-top: min(0.5rem, 1vh);
  text-align: right;
  position: absolute;
  bottom: -1.5rem;
  right: 0;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const GeneralErrorMessage = styled(ErrorMessage)`
  position: static;
  justify-content: flex-end;
  margin-bottom: min(1rem, 2vh);
  color: #ff4444;
  font-size: min(0.875rem, 1.5vw);
`;

const SuccessMessage = styled(GeneralErrorMessage)`
  color: #000049;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: min(2rem, 4vh);
`;

const SubmitButton = styled.button`
  width: min(35%, 200px);
  padding: min(1rem, 2vh) min(2rem, 4vw);
  border: 2px solid white;
  border-radius: 2rem;
  background: transparent;
  color: white;
  font-size: min(1.1rem, 2vw);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  line-height: 1;

  @media (max-width: 1600px) {
    width: min(33%, 190px);
    padding: min(0.9rem, 1.8vh) min(1.8rem, 3.6vw);
    font-size: min(1.05rem, 1.9vw);
  }

  @media (max-width: 1400px) {
    width: min(30%, 180px);
    padding: min(0.8rem, 1.6vh) min(1.6rem, 3.2vw);
    font-size: min(1rem, 1.8vw);
  }

  @media (max-width: 1200px) {
    width: min(28%, 170px);
    padding: min(0.7rem, 1.4vh) min(1.4rem, 2.8vw);
    font-size: min(0.95rem, 1.7vw);
  }

  @media (max-width: 1000px) {
    width: min(25%, 160px);
    padding: min(0.6rem, 1.2vh) min(1.2rem, 2.4vw);
    font-size: min(0.9rem, 1.6vw);
  }

  @media (max-width: 800px) {
    width: min(22%, 150px);
    padding: min(0.5rem, 1vh) min(1rem, 2vw);
    font-size: min(0.85rem, 1.5vw);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegisterLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-size: min(0.9rem, 1.8vw);
  margin-top: min(1rem, 2vh);
  opacity: 0.8;
  transition: opacity 0.3s;
  text-align: right;
  display: block;

  &:hover {
    opacity: 1;
  }
`;



const ErrorIconSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 7V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="12" cy="16" r="1" fill="currentColor"/>
  </svg>
);

const EyeIconSvg = ({ isVisible }) =>
  isVisible ? (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
        fill="currentColor"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
        fill="currentColor"
      />
    </svg>
  );

const LoginForm = () => {
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOfferPopup, setShowOfferPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState(null);
  const [domValues, setDomValues] = useState({ id_number: '', password: '' }); // Para forzar re-render
  const [autofillStatus, setAutofillStatus] = useState({ id_number: false, password: false }); // Para trackear autocomplete

  // Determinar el modo basado en la ruta
  const mode = location.pathname === '/forgot-password' ? 'forgot' :
               location.pathname === '/reset-password' ? 'reset' : 'login';
  
  const token = searchParams.get('token');


  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (mode === 'reset' && !token) {
      setError('Enlace de recuperación inválido');
    }
  }, [mode, token]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Validar token de oferta si existe en la URL
  useEffect(() => {
    const validateOfferTokenFromURL = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const offerToken = urlParams.get('token');
      
      // Intentar obtener el token de sessionStorage (en caso de redirección)
      const storedToken = sessionStorage.getItem('offerToken');
      const tokenToUse = offerToken || storedToken;
      
      if (tokenToUse) {
        try {
          const response = await validateOfferToken(tokenToUse);
          
          if (response.requiresLogin && response.showOfferPopup) {
            // Format the popup message
            const formatPopupMessage = (message) => {
              if (!message) return message;

              // 1. Bold text from "has recibido" to "de la controversia" including the reason
              let formattedMessage = message.replace(
                /(has recibido.*?de la controversia\s+[^.\n]*)/gi,
                '<strong>$1</strong>'
              );
              
              // 2. Bold text "plataforma de Ofertas Vinculantes"
              formattedMessage = formattedMessage.replace(
                /(plataforma de Ofertas Vinculantes)/gi,
                '<strong>$1</strong>'
              );
              
              // 3. Move "Para visualizar" whole line to next line
              formattedMessage = formattedMessage.replace(
                /(\.\s*)(Para visualizar.*?)/gi,
                '$1<br><br>$2'
              );
              
              return formattedMessage;
            };
  
            setPopupData({
              popupMessage: {
                ...response.popupMessage,
                message: formatPopupMessage(response.popupMessage?.message)
              },
              offerInfo: response.offer
            });
            setShowOfferPopup(true);
            
            // Limpiar el token guardado después de mostrar el popup
            sessionStorage.removeItem('offerToken');
          }
        } catch (error) {
          console.error('Error validating offer token:', error);
        }
      }
    };

    validateOfferTokenFromURL();
  }, []);

  // Esquemas de validación según el modo
  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email('Formato de email inválido')
      .required('El email es requerido')
  });

  const resetPasswordSchema = Yup.object().shape({
    newPassword: Yup.string()
      .matches(/^[a-zA-Z0-9]{8,16}$/, 'La contraseña debe tener entre 8 y 16 caracteres alfanuméricos')
      .required('La nueva contraseña es requerida'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], 'Las contraseñas no coinciden')
      .required('La confirmación de contraseña es requerida')
  });

  const getInitialValues = () => {
    switch (mode) {
      case 'forgot':
        return { email: "" };
      case 'reset':
        return { newPassword: "", confirmPassword: "" };
      default:
        return { id_number: "", password: "" };
    }
  };

  const getValidationSchema = () => {
    switch (mode) {
      case 'forgot':
        return forgotPasswordSchema;
      case 'reset':
        return resetPasswordSchema;
      default:
        return loginSchema;
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      setSuccess(false);

      try {
        if (mode === 'forgot') {
          // Solicitar recuperación de contraseña
          const { default: api } = await import('../services/api');
          const response = await api.post('/password-reset', values);
          setSuccess(true);
        } else if (mode === 'reset') {
          // Cambiar contraseña con token
          if (!token) {
            setError('Enlace de recuperación inválido');
            return;
          }

          const { default: api } = await import('../services/api');
          const response = await api.post('/password-reset/verify', {
            token: token,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword
          });
          
          setSuccess(true);
          // Redirigir al login después de 3 segundos
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          // Login normal
          // Generar URL de certificado
          const { certificateUrl } = await generateCertUrl();

          // Hacer POST directo a la URL con certificado SSL
          // El navegador automáticamente solicitará el certificado
          const loginUrl = certificateUrl.replace('/api/registro-cert', '/api/login-cert');
          const response = await fetch(loginUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(values),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Error en el login');
          }

          const result = await response.json();

          // Si hay token, guardarlo
          if (result.token) {
            localStorage.setItem("token", result.token);
            // Configurar token en axios para futuras peticiones
            const { default: api } = await import('../services/api');
            api.defaults.headers.common['Authorization'] = `Bearer ${result.token}`;
          }

          // Verificar si hay una redirección pendiente guardada en sessionStorage ANTES de actualizar la autenticación
          const pendingRedirectUrl = sessionStorage.getItem("pendingRedirectUrl");
          const pendingOfferToken = sessionStorage.getItem("pendingOfferToken");
          
          // Actualizar estado de autenticación
          await login();
          
          if (pendingRedirectUrl && pendingOfferToken) {
            // Limpiar los datos de sessionStorage
            sessionStorage.removeItem("pendingRedirectUrl");
            sessionStorage.removeItem("pendingOfferToken");
            
            // Redirigir a la URL pendiente
            navigate(pendingRedirectUrl);
          } else {
            // Redirección por defecto
            navigate("/offers");
          }
        }
      } catch (error) {
        console.error('Error:', error);
        if (mode === 'login' && error.message.includes('certificado')) {
          setError("Error con el certificado digital: " + error.message);
        } else if (mode === 'login') {
          setError("No se pudo iniciar sesión. Verifica tus credenciales e inténtalo de nuevo.");
        } else if (mode === 'forgot') {
          setError(error.response?.data?.message || 'Error al enviar el correo de recuperación');
        } else if (mode === 'reset') {
          setError(error.response?.data?.message || 'Error al cambiar la contraseña');
        } else {
          setError('Error de conexión. Inténtalo de nuevo.');
        }
      } finally {
        setLoading(false);
      }
    },
  });

  // Detectar autocomplete y sincronizar con Formik
  useEffect(() => {
    const triggerAutocompleteConfirmation = () => {
      const idInput = document.getElementById('id_number');
      const passwordInput = document.getElementById('password');
      
      if (idInput && passwordInput) {
        const beforeClick = {
          idHasAutofill: idInput.matches && idInput.matches(':-webkit-autofill'),
          passwordHasAutofill: passwordInput.matches && passwordInput.matches(':-webkit-autofill')
        };
        
        // Actualizar estado de autocomplete para forzar re-render
        setAutofillStatus({
          id_number: beforeClick.idHasAutofill,
          password: beforeClick.passwordHasAutofill
        });
        
        // Simular clic global para confirmar el autocomplete de Google
        document.body.click();
        
        // Después del clic, verificar si aparecieron valores
        setTimeout(() => {
          const afterClick = {
            idHasAutofill: idInput.matches && idInput.matches(':-webkit-autofill'),
            passwordHasAutofill: passwordInput.matches && passwordInput.matches(':-webkit-autofill')
          };
          
          // Actualizar estado para forzar re-render
          const currentDomValues = {
            id_number: idInput.value || '',
            password: passwordInput.value || ''
          };
          setDomValues(currentDomValues);
          
          // Actualizar estado de autocomplete también
          setAutofillStatus({
            id_number: afterClick.idHasAutofill,
            password: afterClick.passwordHasAutofill
          });
          
          const idValue = idInput.value;
          const passwordValue = passwordInput.value;
          
          // Si hay valores después del clic, actualizarlos en Formik
          if (idValue && idValue !== formik.values.id_number) {
            formik.setFieldValue('id_number', idValue);
            formik.setFieldTouched('id_number', true, false);
          }
          if (passwordValue && passwordValue !== formik.values.password) {
            formik.setFieldValue('password', passwordValue);
            formik.setFieldTouched('password', true, false);
          }
        }, 50);
      }
    };

    // Ejecutar después del montaje y con algunos retrasos para cubrir diferentes navegadores
    const timeouts = [
      setTimeout(triggerAutocompleteConfirmation, 100),
      setTimeout(triggerAutocompleteConfirmation, 500),
      setTimeout(triggerAutocompleteConfirmation, 1000),
      setTimeout(triggerAutocompleteConfirmation, 2000)
    ];

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, []);

  // NO agregar estilos CSS que interfieren con el autocomplete

  const isFormValid = () => {
    if (mode === 'forgot') {
      return formik.values.email !== "" && !formik.errors.email;
    } else if (mode === 'reset') {
      return (
        formik.values.newPassword !== "" && 
        formik.values.confirmPassword !== "" && 
        !formik.errors.newPassword && 
        !formik.errors.confirmPassword
      );
    } else {
      // Login normal
      // Obtener valores tanto de Formik como del DOM
      const formikIdValue = formik.values.id_number;
      const formikPasswordValue = formik.values.password;
      
      const idInput = document.getElementById('id_number');
      const passwordInput = document.getElementById('password');
      const domIdValue = idInput?.value || '';
      const domPasswordValue = passwordInput?.value || '';
      
      // Detectar si tienen autocomplete de Google
      const idHasAutofill = idInput?.matches && idInput.matches(':-webkit-autofill');
      const passwordHasAutofill = passwordInput?.matches && passwordInput.matches(':-webkit-autofill');
      
      // Usar el valor que no esté vacío (priorizar Formik, fallback a DOM, fallback a autocomplete)
      const finalIdValue = formikIdValue || domIdValue || (idHasAutofill ? 'AUTOCOMPLETE_VALUE' : '');
      const finalPasswordValue = formikPasswordValue || domPasswordValue || (passwordHasAutofill ? 'AUTOCOMPLETE_VALUE' : '');
      
      // Verificar validación de Formik solo para los campos que están en Formik
      const hasFormikErrors = formik.errors.id_number || formik.errors.password;
      
      return (
        !hasFormikErrors &&
        finalIdValue !== "" &&
        finalPasswordValue !== ""
      );
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleClosePopup = () => {
    setShowOfferPopup(false);
    setPopupData(null);
  };



  if (isMobile) {
    return <MobileLoginForm />;
  }

  return (
    <FormContainer $loading={loading}>
      <TopLogo src={sidebarLogo} alt="Logo" />
      <ContentWrapper>
        <LeftSection>
          <HeaderLogo src={headerLogo} alt="Header Logo" />
          <HeaderContent>
            <FormTitle>
              {mode === 'forgot' ? 'Recuperar acceso' :
               mode === 'reset' ? 'Nueva contraseña' : 'Iniciar sesión'}
            </FormTitle>
            <FormSubtitle>
              {mode === 'forgot' ? 
                `Introduce tu correo electrónico y te enviaremos\nun enlace para restablecer tu contraseña` :
               mode === 'reset' ?
                `Introduce tu nueva contraseña.\nDebe ser diferente a la anterior` :
                `Accede a tu cuenta para enviar, negociar\no validar ofertas vinculantes`}
            </FormSubtitle>
          </HeaderContent>
        </LeftSection>
        <RightSection>
          {success ? (
            <div style={{ textAlign: 'center' }}>
              <SuccessMessage>
                {mode === 'forgot' ?
                  'Se ha enviado un correo con las instrucciones para recuperar tu contraseña. Revisa tu bandeja de entrada y la carpeta de spam.' :
                  '¡Contraseña actualizada correctamente! Serás redirigido al inicio de sesión en unos segundos.'}
              </SuccessMessage>
              <RegisterLink to="/login">
                {mode === 'reset' ? 'Ir al inicio de sesión ahora' : 'Volver al inicio de sesión'}
              </RegisterLink>
            </div>
          ) : (
            <form onSubmit={formik.handleSubmit}>
              {mode === 'login' && (
                <>
                  <HorizontalField>
                    <div className="input-row">
                      <Label htmlFor="id_number">DNI, NIE</Label>
                      <Input
                        id="id_number"
                        name="id_number"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.id_number}
                      />
                    </div>
                    {formik.touched.id_number && formik.errors.id_number && (
                      <ErrorMessage>
                        <ErrorIconSvg />
                        {formik.errors.id_number}
                      </ErrorMessage>
                    )}
                  </HorizontalField>

                  <HorizontalField>
                    <div className="input-row">
                      <Label htmlFor="password">Contraseña</Label>
                      <PasswordField>
                        <PasswordInput
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.password}
                        />
                        <EyeIcon type="button" onClick={togglePasswordVisibility}>
                          <EyeIconSvg isVisible={!showPassword} />
                        </EyeIcon>
                      </PasswordField>
                    </div>
                    {formik.touched.password && formik.errors.password && (
                      <ErrorMessage>
                        <ErrorIconSvg />
                        {formik.errors.password}
                      </ErrorMessage>
                    )}
                  </HorizontalField>
                </>
              )}

              {mode === 'forgot' && (
                <HorizontalField>
                  <div className="input-row">
                    <Label htmlFor="email">Correo electrónico</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="tu@email.com"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.email}
                    />
                  </div>
                  {formik.touched.email && formik.errors.email && (
                    <ErrorMessage>
                      <ErrorIconSvg />
                      {formik.errors.email}
                    </ErrorMessage>
                  )}
                </HorizontalField>
              )}

              {mode === 'reset' && (
                <>
                  <HorizontalField>
                    <div className="input-row">
                      <Label htmlFor="newPassword">Nueva contraseña</Label>
                      <PasswordField>
                        <PasswordInput
                          id="newPassword"
                          name="newPassword"
                          type={showPassword ? "text" : "password"}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.newPassword}
                        />
                        <EyeIcon type="button" onClick={togglePasswordVisibility}>
                          <EyeIconSvg isVisible={!showPassword} />
                        </EyeIcon>
                      </PasswordField>
                    </div>
                    {formik.touched.newPassword && formik.errors.newPassword && (
                      <ErrorMessage>
                        <ErrorIconSvg />
                        {formik.errors.newPassword}
                      </ErrorMessage>
                    )}
                  </HorizontalField>

                  <HorizontalField>
                    <div className="input-row">
                      <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                      <PasswordField>
                        <PasswordInput
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.confirmPassword}
                        />
                        <EyeIcon type="button" onClick={toggleConfirmPasswordVisibility}>
                          <EyeIconSvg isVisible={!showConfirmPassword} />
                        </EyeIcon>
                      </PasswordField>
                    </div>
                    {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                      <ErrorMessage>
                        <ErrorIconSvg />
                        {formik.errors.confirmPassword}
                      </ErrorMessage>
                    )}
                  </HorizontalField>
                </>
              )}

              <ButtonContainer>
                {error && (
                  <GeneralErrorMessage>
                    <ErrorIconSvg />
                    {error}
                  </GeneralErrorMessage>
                )}
                <SubmitButton type="submit" disabled={loading || !isFormValid()}>
                  {loading ? 
                    (mode === 'forgot' ? 'Enviando...' : 
                     mode === 'reset' ? 'Actualizando...' : 'Iniciando sesión...') :
                    (mode === 'forgot' ? 'Recuperar' :
                     mode === 'reset' ? 'Actualizar' : 'Iniciar sesión')}
                </SubmitButton>
                
                {mode === 'login' && (
                  <>
                    <RegisterLink to="/register">
                      ¿No tienes cuenta? Regístrate aquí
                    </RegisterLink>
                    
                    <RegisterLink to="/forgot-password">
                      ¿Olvidaste tu contraseña?
                    </RegisterLink>
                  </>
                )}

                {(mode === 'forgot' || mode === 'reset') && (
                  <RegisterLink to="/login">
                    Volver al inicio de sesión
                  </RegisterLink>
                )}
              </ButtonContainer>
            </form>
          )}
        </RightSection>
      </ContentWrapper>
      <LoginFooter />
      
      {/* Popup de oferta vinculante */}
      <OfferPopup
        isOpen={showOfferPopup}
        onClose={handleClosePopup}
        popupMessage={popupData?.popupMessage}
        offerInfo={popupData?.offerInfo}
      />
    </FormContainer>
  );
};

export default LoginForm;
